import {
  FeedbackInviteMode,
  FeedbackQuestionType,
} from '@up2stay/up2promo-api-client';
import type { ProductDto } from '@up2stay/up2promo-api-client';
import { z } from 'zod';

import type {
  ProductFeedbackQuestionDraft,
  ProductFormLocation,
  ProductFormValues,
} from './product-form.types';
import {
  canSubmitForRoutingMode,
  defaultTargetKindForProduct,
  inferRoutingMode,
  hasFeedbackFunnel,
  isTargetUrlFieldVisible,
  isValidHttpUrl,
  type ProductRoutingMode,
} from './product-routing.utils';

export type ProductDetailFormValues = ProductFormValues & {
  routingMode: ProductRoutingMode;
};

const routingModeSchema = z.enum([
  'profile',
  'location',
  'instagramUsername',
  'tiktokUsername',
  'customUrl',
  'behaviourSelect',
]);

function feedbackQuestionsFromProduct(
  product: ProductDto,
): ProductFeedbackQuestionDraft[] {
  return (product.feedback?.questions ?? []).map((q, index) => ({
    key: q.id?.trim() || `q-${index}`,
    id: q.id,
    type: q.type || FeedbackQuestionType.SHORT_TEXT,
    label: q.label ?? '',
    required: q.required === true,
    optionsText: (q.options ?? []).join('\n'),
  }));
}

/** Read adapter: nested product (ADR-0026, DEV-794) → flat form values. */
export function formValuesFromProduct(product: ProductDto): ProductFormValues {
  const { target, feedback } = product;
  return {
    description: product.label ?? '',
    targetKind: defaultTargetKindForProduct(null, target?.kind),
    locationId: target?.locationId ?? null,
    userId: target?.userId ?? null,
    targetUrl: target?.url ?? '',
    feedbackMode: feedback?.mode ?? FeedbackInviteMode.DIRECT_REVIEW,
    reviewThreshold: feedback?.reviewThreshold ?? 4,
    feedbackQuestions: feedbackQuestionsFromProduct(product),
  };
}

export function productDetailDefaults(
  product: ProductDto,
  knownProductType?: string | null,
): ProductDetailFormValues {
  const values = formValuesFromProduct(product);
  return {
    ...values,
    routingMode: inferRoutingMode(values, knownProductType),
  };
}

type ProductFormSchemaMessages = {
  descriptionRequired?: string;
  targetUrlRequired?: string;
  invalidTargetUrl?: string;
  feedbackFormRequired?: string;
};

export function createProductDetailFormSchema(options: {
  knownProductType?: string | null;
  locations: ProductFormLocation[];
  messages?: ProductFormSchemaMessages;
}) {
  const { knownProductType, locations, messages } = options;

  return z
    .object({
      description: z.string(),
      targetKind: z.string(),
      locationId: z.string().nullable(),
      userId: z.string().nullable(),
      targetUrl: z.string(),
      feedbackMode: z.nativeEnum(FeedbackInviteMode),
      reviewThreshold: z.number().min(1).max(5),
      feedbackQuestions: z.array(
        z.object({
          key: z.string(),
          id: z.string().optional(),
          type: z.nativeEnum(FeedbackQuestionType),
          label: z.string(),
          required: z.boolean(),
          optionsText: z.string(),
        }),
      ),
      routingMode: routingModeSchema,
    })
    .superRefine((data, ctx) => {
      if (!data.description.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['description'],
          message: messages?.descriptionRequired ?? 'Description is required.',
        });
      }

      const values: ProductFormValues = {
        description: data.description,
        targetKind: data.targetKind,
        locationId: data.locationId,
        userId: data.userId,
        targetUrl: data.targetUrl,
        feedbackMode: data.feedbackMode,
        reviewThreshold: data.reviewThreshold,
        feedbackQuestions: data.feedbackQuestions,
      };

      if (
        hasFeedbackFunnel(values) &&
        data.feedbackMode === FeedbackInviteMode.FEEDBACK_ONLY &&
        data.feedbackQuestions.length === 0
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['feedbackQuestions'],
          message:
            messages?.feedbackFormRequired ??
            'Choose a feedback form from your library.',
        });
      }

      if (
        !canSubmitForRoutingMode({
          description: data.description,
          mode: data.routingMode,
          values,
          knownProductType,
          locations,
        })
      ) {
        if (data.routingMode === 'profile' && !data.userId) {
          ctx.addIssue({
            code: 'custom',
            path: ['userId'],
            message: 'Profile user is required.',
          });
        }

        if (data.routingMode === 'location' && !data.locationId) {
          ctx.addIssue({
            code: 'custom',
            path: ['locationId'],
            message: 'Location is required.',
          });
        }

        if (isTargetUrlFieldVisible(data.routingMode, data.targetKind)) {
          const trimmed = data.targetUrl.trim();
          if (!trimmed) {
            ctx.addIssue({
              code: 'custom',
              path: ['targetUrl'],
              message: messages?.targetUrlRequired ?? 'Enter a link URL.',
            });
          } else if (!isValidHttpUrl(trimmed)) {
            ctx.addIssue({
              code: 'custom',
              path: ['targetUrl'],
              message:
                messages?.invalidTargetUrl ??
                'Enter a valid URL (for example example.com or https://example.com).',
            });
          }
        }
      }
    });
}
